import UserProfile from "@/models/UserProfile";
import { useUserProfileApi } from "@/setup/api";
import { REFERENCIAL_APPLICATION_NAME, REFERENCIAL_NAME_BY_COUNTRY } from "@/utils/sections";
import { defineStore } from "pinia";
import { userClient } from "@/api/userManagementSetup";

export const useUserStore = defineStore("userStore", {
  state: () => ({
    userProfile: new UserProfile(),
    userProfileLoaded: false,
    usersProfiles: [],
    usersProfilesMap: {},
  }),
  getters: {
    isLoaded: (state) => !!state.userProfile.email,
    getUserGroups: (state) => state.userProfile.groups,
    seeManagerDashboard: (state) =>
      state.userProfile.isDev() ||
      state.userProfile.isAgencyManager() ||
      state.userProfile.isActivityDirector() ||
      state.userProfile.isAgencyGroupManager() ||
      state.userProfile.isNationalManager(),

    isExternalUser: (state) => state.userProfile.isExternal(),
    isSuperUser: (state) => state.userProfile.is_superuser,
    isDev: (state) => state.userProfile.isDev(),
    isTechnicalDirection: (state) => state.userProfile.isTechnicalDirection(),
    isSocotecUser: (state) => state.userProfile.isFromSocotec(),
    isDevOrSuperUser: (state) => state.userProfile.isDevOrSuperUser(),

    seeManagerMessages: (state) =>
      state.userProfile.isAgencyManager() || state.userProfile.isAgencyGroupManager(),

    isUserInGroup: (state) => (groupName) => {
      if (state.isDevOrSuperUser) {
        return true;
      }
      return state.userProfile.groups.includes(groupName);
    },

    seeEmployeeDashboard: (state) => state.userProfile.isFromSocotec(),

    isPilotOfOneOrMoreProjects: (state) => (entity) =>
      state.userProfile[`is_pilot_of_one_or_more_${entity.toLowerCase()}_projects`],
    isInCorrectGroupsOrPilot: (state) => (entity) => {
      entity = entity?.toUpperCase();
      return (
        state.isUserInGroup(entity) ||
        state.isPilotOfOneOrMoreProjects(entity) ||
        state.userProfile.isHierarchicalManager() ||
        state.userProfile.isTechnicalDirection()
      );
    },

    getCurrentUserProfile: (state) => state.userProfile,
    getUserProfileByUuid: (state) => (userManagementUuid) => {
      return state.usersProfiles.find((user) => user.usermanagement_uuid === userManagementUuid);
    },

    getCurrentUserIrisAccessToken: (state) => state.userProfile.iris_access_token,
    isMultilanguageActivated: (state) => state.userProfile.is_multi_language_activated,

    getReferencialApplicationNameByUserCountry: (state) => {
      // INFO - AM - 28/07/2023 - Don't use this method outside technical documentation because it doesn't support rapsosecu. (Rapsosecu doesn't use Technical documentation)
      return (
        REFERENCIAL_NAME_BY_COUNTRY[state.userProfile.agency.country] ||
        REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE
      );
    },
    canUseCycleLong: (state) => state.isDev,
  },
  actions: {
    async fetchRapsotecUser() {
      const userProfile = await useUserProfileApi().me();
      this.setRapsotecUser(userProfile);
      return userProfile;
    },
    async fetchRapsotecUserProfiles(params) {
      const userProfile = await useUserProfileApi().list(params);
      const { results = [] } = userProfile;
      this.setRapsotecUsersProfiles(results);
      return userProfile;
    },

    async updateUserProfile(userProfileData) {
      const userProfile = await useUserProfileApi().updateMe({
        patchedSelfUserProfileRequest: userProfileData,
      });
      this.setRapsotecUser(userProfile);
    },
    async fetchUsers(params = {}) {
      const response = await userClient.list(params);
      return response.results || [];
    },

    setRapsotecUser(userProfile) {
      this.userProfile.setData(userProfile);
      this.userProfileLoaded = true;
    },
    setRapsotecUsersProfiles(usersProfiles) {
      const users = usersProfiles.map((user) => {
        return new UserProfile(user);
      });

      const usersProfilesMap = {};
      usersProfiles.forEach((user) => {
        usersProfilesMap[user.user_management_uuid] = user;
      });
      this.usersProfilesMap = usersProfilesMap;
      this.usersProfiles = users;
    },
  },
});
